import axios from "axios";
import { Buffer } from 'buffer';
import { ApiResponseModel } from "../Models/ApiResponseModel";
import LocalStorageHelper from "../Utilities/Helpers/LocalStorageHelper";
import { ErrorMessage } from "../Constants/Messages";
import { Authentication } from "../Models/Authentication/Authentication";
import { AuthenticationField, ImageFileField } from "../Utilities/Enums/ParametersEnum";
import { RouteEnum } from '../Utilities/Enums/RouteEnums';

const expiredTokenBroadcast = new BroadcastChannel('online-session');
const _userData: Authentication = LocalStorageHelper.getUserData();
const axiosClient = axios.create();

axiosClient.defaults.baseURL = process.env.REACT_APP_API_URL + '/api/';
// axiosClient.defaults.timeout = 2000 * 5;
axiosClient.defaults.timeout = 2000 * 50;
// axiosClient.defaults.withCredentials = (localStorage.getItem('userData') !== null ? true : false);

axiosClient.defaults.headers["Content-Type"] = 'application/json';
axiosClient.defaults.headers["Accept"] = 'application/json';
axiosClient.defaults.headers["Access-Control-Allow-Origin"] = '*';
axiosClient.defaults.headers["ngrok-skip-browser-warning"] = '69420';

function validateResponse(response: any, fromGetFileRequestWithToken = false): ApiResponseModel {
    let _response = {...new ApiResponseModel()};
    if (response == null || response.data == null) {
        throw Error(ErrorMessage.NO_RESPONSE_SERVER);
    }

    if(fromGetFileRequestWithToken) {
        const _file = `data:${response.headers['content-type']};base64,${Buffer.from(response.data, 'binary').toString('base64')}`;

        _response = {
            ..._response,
            data: _file,
            isSuccess: true,
        };
    } else {
        _response = {
            ..._response,
            data: response?.data,
            // isSuccess: true,
            isSuccess: response?.data?.success ?? false,
            message: response?.data?.message ?? null,
            count: response?.data?.data?.metaData?.totalCount ?? 0,
        }
    }

    return _response;
}

function preettifyErrorMessage(error: any) {
    if (error && error.response && error.response.status) {
        if (error.response.status == 401) {
            LocalStorageHelper.removeToken()
            LocalStorageHelper.removeUserData()
            LocalStorageHelper.removeBrowsingHistoryData()
            expiredTokenBroadcast.postMessage({ type: 'SESSION_EXPIRED' });

            window.location.replace(RouteEnum.LOGIN); // force redirect to login page when session is expired

            return ErrorMessage.EXPIRED_LOGGEDIN_SESSION;
        } else if (error.response.status == 404) {
            return ErrorMessage.URL_NOT_FOUND;
        } else if (error.response.status == 400) {
            return (error?.response?.data?.message && error.response.data.message != "" ? error.response.data.message : ErrorMessage.INCORRECT_DATA_SUBMITTED);
        } else if (error.response.status >= 500 && error.response.status <= 599) {
            return ErrorMessage.SERVER_ERROR;
        }
    }
    return error.message;
}

export async function getRequest(URL: any) {
    axiosClient.defaults.headers["Authorization"] = (LocalStorageHelper.isLoggedIn() ? "Bearer " + _userData?.[AuthenticationField.TOKEN] : "");
    axiosClient.defaults.headers["Content-Type"] = 'application/json';
    axiosClient.defaults.headers["Accept"] = 'application/json';

    return axiosClient
        .get(`/${URL}`)
        .then((response) => {
            return validateResponse(response);
        })
        .catch((err) => {
            return {
                data: null,
                isSuccess: false,
                statusCode: err?.response?.status ?? 201,
                message: preettifyErrorMessage(err),
                count: 0
            };
        });
}

export async function postRequest(URL: any, payload: any = null) {
    axiosClient.defaults.headers["Authorization"] = (LocalStorageHelper.isLoggedIn() ? "Bearer " + _userData?.[AuthenticationField.TOKEN] : "");
    axiosClient.defaults.headers["Content-Type"] = 'application/json';
    axiosClient.defaults.headers["Accept"] = 'application/json';

    return axiosClient
        .post(`/${URL}`, payload)
        .then((response) => {
            return validateResponse(response);
        })
        .catch((err) => {
            return {
                data: null,
                isSuccess: false,
                statusCode: err?.response?.status ?? 201,
                message: preettifyErrorMessage(err),
                count: 0
            };
        });
}

export async function postUploadRequest(URL: any, payload: any) {
    axiosClient.defaults.headers["Authorization"] = (LocalStorageHelper.isLoggedIn() ? "Bearer " + _userData?.[AuthenticationField.TOKEN] : "");
    axiosClient.defaults.headers["Content-Type"] = 'multipart/form-data';
    axiosClient.defaults.headers["Accept"] = 'multipart/form-data';

    return axiosClient
        .post(`/${URL}`, payload)
        .then((response) => {
            return validateResponse(response);
        })
        .catch((err) => {
            return {
                data: null,
                isSuccess: false,
                statusCode: err?.response?.status ?? 201,
                message: preettifyErrorMessage(err),
                count: 0
            };
        });
}

export async function putRequest(URL: any, payload: any) {
    axiosClient.defaults.headers["Authorization"] = (LocalStorageHelper.isLoggedIn() ? "Bearer " + _userData?.[AuthenticationField.TOKEN] : "");
    axiosClient.defaults.headers["Content-Type"] = 'application/json';
    axiosClient.defaults.headers["Accept"] = 'application/json';

    return axiosClient
        .put(`/${URL}`, payload)
        .then((response) => {
            return validateResponse(response);
        })
        .catch((err) => {
            return {
                data: null,
                isSuccess: false,
                statusCode: err?.response?.status ?? 201,
                message: preettifyErrorMessage(err),
                count: 0
            };
        });
}

export async function deleteRequest(URL: any) {
    axiosClient.defaults.headers["Authorization"] = (LocalStorageHelper.isLoggedIn() ? "Bearer " + _userData?.[AuthenticationField.TOKEN] : "");
    axiosClient.defaults.headers["Content-Type"] = 'application/json';
    axiosClient.defaults.headers["Accept"] = 'application/json';

    return axiosClient
        .delete(`/${URL}`)
        .then((response) => {
            return validateResponse(response);
        })
        .catch((err) => {
            return {
                data: null,
                isSuccess: false,
                statusCode: err?.response?.status ?? 201,
                message: preettifyErrorMessage(err),
                count: 0
            };
        });
}

export async function importRequest(URL: any, payload: any) {
    axiosClient.defaults.headers["Authorization"] = (LocalStorageHelper.isLoggedIn() ? "Bearer " + _userData?.[AuthenticationField.TOKEN] : "");
    axiosClient.defaults.headers["Content-Type"] = 'multipart/form-data';
    axiosClient.defaults.headers["Accept"] = 'multipart/form-data';

    const formData = new FormData();
    formData.append("file", payload);

    return axiosClient
        .post(`/${URL}`, formData)
        .then((response) => {
            return validateResponse(response);
        })
        .catch((err) => {
            return {
                data: null,
                isSuccess: false,
                statusCode: err?.response?.status ?? 201,
                message: preettifyErrorMessage(err),
                count: 0
            };
        });
}

export async function uploadRequest(URL: any, payload: any) {
    axiosClient.defaults.headers["Authorization"] = (LocalStorageHelper.isLoggedIn() ? "Bearer " + _userData?.[AuthenticationField.TOKEN] : "");
    axiosClient.defaults.headers["Content-Type"] = 'multipart/form-data';
    axiosClient.defaults.headers["Accept"] = 'multipart/form-data';

    return axiosClient
        .put(`/${URL}`, payload)
        .then((response) => {
            return validateResponse(response);
        })
        .catch((err) => {
            return {
                data: null,
                isSuccess: false,
                statusCode: err?.response?.status ?? 201,
                message: preettifyErrorMessage(err),
                count: 0
            };
        });
}

// export async function getFileRequestWithToken(URL: any, cancelSourceToken = "") {
export async function getFileRequestWithToken(URL: any) {
    axiosClient.defaults.headers["Authorization"] = (LocalStorageHelper.isLoggedIn() ? "Bearer " + _userData?.[AuthenticationField.TOKEN] : "");
    axiosClient.defaults.headers["Content-Type"] = 'application/json';
    axiosClient.defaults.headers["Accept"] = 'application/json';
    axiosClient.defaults.timeout = 6000000;

    return axiosClient
        .get(`/${URL}`, {
            // cancelToken: cancelSourceToken,
            responseType: 'arraybuffer',
        })
        .then((response) => {
            return validateResponse(response, true);
        })
        .catch((err) => {
            return { errorCode: err && err.response && err.response.status ? err.response.status : 201, errorMessage: preettifyErrorMessage(err) };
        });
}